import React, { useState, useEffect, useRef } from 'react';
import { X, MessageSquare, CreditCard, AlertCircle } from 'lucide-react';
import { useCalculateSmsCost, useInitiateSmsRecharge, useSmsPricing } from '../hooks/useSmsQuota';

export default function SmsRechargeModal({ isOpen, onClose, onSuccess }) {
  const [smsQuantity, setSmsQuantity] = useState(10);
  const [step, setStep] = useState('input'); // 'input', 'confirming', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const { data: pricingData } = useSmsPricing();
  const calculateCost = useCalculateSmsCost();
  const initiateRecharge = useInitiateSmsRecharge();
  const calculateCostRef = useRef(calculateCost);

  // Update ref when calculateCost changes
  useEffect(() => {
    calculateCostRef.current = calculateCost;
  }, [calculateCost]);

  const pricing = pricingData?.data;
  const minimumQuantity = pricing?.minimum_recharge || 10;

  // Calculate cost only when quantity changes, not on every render
  useEffect(() => {
    if (smsQuantity >= minimumQuantity && !calculateCostRef.current.isLoading) {
      const timeoutId = setTimeout(() => {
        calculateCostRef.current.mutate(smsQuantity);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, [smsQuantity, minimumQuantity]); // Removed calculateCost from dependencies

  const handleQuantityChange = (e) => {
    const value = parseInt(e.target.value) || 0;
    setSmsQuantity(Math.max(minimumQuantity, value));
  };

  const handleRecharge = async () => {
    if (smsQuantity < minimumQuantity) {
      setErrorMessage(`Minimum recharge quantity is ${minimumQuantity} SMS`);
      return;
    }

    setStep('confirming');
    setErrorMessage('');

    try {
      await initiateRecharge.mutateAsync(smsQuantity);
      setStep('success');
      if (onSuccess) onSuccess();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Failed to initiate recharge');
      setStep('error');
    }
  };

  const handleClose = () => {
    setStep('input');
    setSmsQuantity(10);
    setErrorMessage('');
    onClose();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
            Recharge SMS
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'input' && (
            <>
              {/* Quantity Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of SMS to recharge
                </label>
                <input
                  type="number"
                  min={minimumQuantity}
                  value={smsQuantity}
                  onChange={handleQuantityChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={`Minimum ${minimumQuantity} SMS`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum recharge: {minimumQuantity} SMS
                </p>
              </div>

              {/* Cost Display */}
              {calculateCost.data?.data && (
                <div className="mb-6 p-4 bg-gray-50 rounded-md">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Cost:</span>
                    <span className="text-lg font-bold text-gray-900">
                      {formatCurrency(calculateCost.data.data.amount)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {smsQuantity} SMS × {pricing?.price_per_sms} {pricing?.currency} per SMS
                  </div>
                </div>
              )}

              {/* Error Display */}
              {errorMessage && (
                <div className="mb-4 p-3 bg-red-50 border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-md">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
                    <span className="text-sm text-red-800">{errorMessage}</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecharge}
                  disabled={calculateCost.isLoading || smsQuantity < minimumQuantity}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                >
                  {calculateCost.isLoading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  Pay with M-Pesa
                </button>
              </div>
            </>
          )}

          {step === 'confirming' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Initiating Payment
              </h3>
              <p className="text-gray-600">
                Please wait while we process your payment request...
              </p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Payment Initiated!
              </h3>
              <p className="text-gray-600 mb-6">
                Check your phone for the M-Pesa prompt to complete the payment.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Payment Failed
              </h3>
              <p className="text-gray-600 mb-6">
                {errorMessage || 'Failed to initiate payment. Please try again.'}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setStep('input')}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 